"use client";

import { CtaPillButton } from "@/components/ui/cta-pill-button";
import {
  DEFAULT_CLASSROOM_BODY,
  DEFAULT_CLASSROOM_TITLE,
  classroomShareUrl,
} from "@/lib/google-classroom-share";
import { absoluteUrl } from "@/lib/seo/site";
import { copyUrlToClipboard } from "@/lib/share";
import { UTM_CAMPAIGN_EDUCATORS, UTM_SITE_SHARE_DEFAULTS, appendUtmParams } from "@/lib/utm";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

function communityHomeUrl(utm_content: string) {
  return appendUtmParams(absoluteUrl("/"), {
    ...UTM_SITE_SHARE_DEFAULTS,
    utm_campaign: UTM_CAMPAIGN_EDUCATORS,
    utm_content,
  });
}

const SHARE_URL_COPY_LINK = communityHomeUrl("copy_link");
const SHARE_URL_EMAIL = communityHomeUrl("email_colleague");
const SHARE_SUBJECT = "PlayMath.org – Kid-loved, teacher-approved math games";
const SHARE_BODY = `I've been using PlayMath.org with my students – fun math games that build confidence and deeper thinking. Free and teacher-approved.\n\nCheck it out: ${SHARE_URL_EMAIL}`;
const MAILTO_HREF = `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${encodeURIComponent(SHARE_BODY)}`;

const COPIED_FEEDBACK_MS = 2500;

export function CommunitySection() {
  const [colleagueLinkCopied, setColleagueLinkCopied] = useState(false);
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyColleagueLink = useCallback(async () => {
    const result = await copyUrlToClipboard(SHARE_URL_COPY_LINK);
    if (!result.success) {
      toast.error("Could not copy link. Please try again.");
      return;
    }
    if (copiedResetTimeoutRef.current) {
      clearTimeout(copiedResetTimeoutRef.current);
    }
    setColleagueLinkCopied(true);
    copiedResetTimeoutRef.current = setTimeout(() => {
      setColleagueLinkCopied(false);
      copiedResetTimeoutRef.current = null;
    }, COPIED_FEEDBACK_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current) {
        clearTimeout(copiedResetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="bg-surface-200">
      <div className="mx-auto px-3 sm:px-4 max-w-[1024px] pt-24 md:pt-28 pb-24 md:pb-28">
        <h2 className="text-2xl md:text-4xl font-extrabold text-black text-center">
          Spread the joy of math
        </h2>
        <p className="mt-4 text-center text-gray-700 max-w-2xl mx-auto">
          PlayMath.org helps kids everywhere discover the fun of math… thanks to teachers like you!
          Share it with a colleague or bring the excitement into your classroom.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 flex flex-col items-center text-center gap-4 shadow-sm">
            <div className="h-28 flex items-center justify-center">
              <img
                src="/images/icons/googleClassroom-icon.svg"
                alt="Google Classroom"
                className="h-28 w-auto max-w-full object-contain"
              />
            </div>
            <h3 className="text-lg font-bold text-black">Add to Google Classroom</h3>
            <p className="text-gray-700 text-sm">Share PlayMath.org with your students!</p>
            <CtaPillButton
              href={classroomShareUrl(communityHomeUrl("google_classroom"), {
                title: DEFAULT_CLASSROOM_TITLE,
                body: DEFAULT_CLASSROOM_BODY,
              })}
              variant="primary"
              size="sm"
            >
              Share Now
            </CtaPillButton>
          </div>
          <div className="bg-white rounded-2xl p-6 flex flex-col items-center text-center gap-4 shadow-sm">
            <div className="h-28 flex items-center justify-center w-full">
              <img
                src="/images/marketing/home/teacher/teacher-sitting-art.webp"
                alt="Teacher sitting illustration"
                className="h-28 w-auto max-w-full object-contain"
              />
            </div>
            <h3 className="text-lg font-bold text-black">Share with a colleague</h3>
            <p className="text-gray-700 text-sm">Email a fellow educator or copy the link.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <CtaPillButton href={MAILTO_HREF} variant="primary" size="sm">
                Email colleague
              </CtaPillButton>
              <CtaPillButton variant="secondary" size="sm" onClick={handleCopyColleagueLink}>
                {colleagueLinkCopied ? "Copied!" : "Copy link"}
              </CtaPillButton>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 flex flex-col items-center text-center gap-4 shadow-sm">
            <div className="h-28 flex items-center justify-center">
              <img
                src="/images/logos/logo-stacked-black.svg"
                alt="PlayMath.org"
                className="h-28 w-auto max-w-full object-contain"
              />
            </div>
            <h3 className="text-lg font-bold text-black">Brand kit</h3>
            <p className="text-gray-700 text-sm">Our logos & other assets for your site.</p>
            <CtaPillButton href="/brand" variant="primary" size="sm">
              View brand kit
            </CtaPillButton>
          </div>
        </div>
      </div>
    </section>
  );
}
